export const ___ = Symbol('partial application placeholder');

/**
 * Take a function with N arguments and partially apply all args
 * except one marked with ___
 *
 * Useful especially for calling functions in pipes:
 * ```
 * declare function promote(userId: string, role: Role);
 * pipe(
 *   findUsersWithExceptionalResults(),
 *   array.map(partialApply(promote, ___, 'manager')),
 * );
 * ```
 */
export const partialApply: {
  <TOmitted, TTail extends unknown[], TReturn>(
    // (f, ___, ...tail)
    f: (c: TOmitted, ...t: TTail) => TReturn,
    placeholder: typeof ___,
    ...t: TTail
  ): (c: TOmitted) => TReturn;
  <TArg1, TOmitted, TTail extends unknown[], TReturn>(
    // (f, arg1, ___, ...tail)
    f: (arg1: TArg1, c: TOmitted, ...t: TTail) => TReturn,
    arg1: TArg1,
    placeholder: typeof ___,
    ...t: TTail
  ): (c: TOmitted) => TReturn;
  <TArg1, TArg2, TOmitted, TTail extends unknown[], TReturn>(
    // (f, arg1, arg2, ___, ...tail)
    f: (arg1: TArg1, arg2: TArg2, c: TOmitted, ...t: TTail) => TReturn,
    arg1: TArg1,
    arg2: TArg2,
    placeholder: typeof ___,
    ...t: TTail
  ): (c: TOmitted) => TReturn;
  // overloads up to third argument are declared.
  // feel free to add more overloads when needed, but make sure to add tests.
} = (f: any, ...args: any[]) => {
  const offset = args.indexOf(___);
  if (offset === -1) {
    throw new Error('this function must receive ___ as one of its arguments');
  }
  const argsBefore = args.slice(0, offset);
  const argsAfter = args.slice(offset + 1);
  return (c: any) => f(...argsBefore, c, ...argsAfter);
};
